import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import api, { asArray } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Category() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, p] = await Promise.all([api.get(`/categories/${slug}`), api.get(`/products?category=${slug}`)]);
      setCategory(c.data);
      setProducts(asArray(p.data));
    })().catch(() => {});
  }, [slug]);

  if (!category) return <div className="max-w-[1400px] mx-auto px-6 py-24"><div className="h-96 bg-ivory animate-pulse" /></div>;

  return (
    <div data-testid="category-page">
      {/* Hero banner */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img src={category.banner_image} alt={category.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/20 via-transparent to-cream" />
        <div className="relative w-full max-w-[1400px] mx-auto px-6 lg:px-12 pb-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <div className="overline text-ivory/85 mb-4"><span className="w-8 h-px bg-gold inline-block mr-3" />The Collection</div>
            <h1 className="font-serif text-ivory text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]">{category.name}</h1>
            <p className="mt-6 text-ivory/85 max-w-2xl text-[17px] leading-relaxed">{category.description}</p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-28">
          <div className="overline text-emerald mb-3">The story</div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">How we make it.</h2>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <p className="text-[16px] leading-relaxed text-charcoal/80">{category.story}</p>
          <Link to="/about" className="mt-8 inline-flex items-center gap-2 group">
            <span className="overline text-charcoal">More from the journal</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Products */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24">
        <div className="flex items-end justify-between mb-10 pb-6 border-b divider-gold">
          <div className="overline text-emerald">The pieces · {products.length}</div>
          <Link to={`/shop?category=${slug}`} className="nav-link overline text-charcoal">All in shop</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-12">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* FAQs */}
      {category.faqs && category.faqs.length > 0 && (
        <section className="bg-ivory/70 py-24">
          <div className="max-w-[900px] mx-auto px-6 lg:px-12">
            <div className="overline text-emerald mb-3 text-center">Questions</div>
            <h2 className="font-serif text-4xl md:text-5xl text-center tracking-tight mb-12">Every piece, answered.</h2>
            <Accordion type="single" collapsible className="border-t divider-gold">
              {category.faqs.map((f, i) => (
                <AccordionItem key={i} value={String(i)} className="border-b divider-gold">
                  <AccordionTrigger className="text-left font-serif text-lg py-6">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-charcoal/70 pb-6 text-[15px]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}
    </div>
  );
}
